import { PLAYER_PUBLIC_SITE_ID } from '../boot/siteId.js';

export const API_SITE = PLAYER_PUBLIC_SITE_ID;
export const SESSION_KEY = 'ninet6_player_session';
export const DEFAULT_LIMIT = 20;
export const DATE_PRESETS = Object.freeze(['today', 'yesterday', 'last7']);
export const DATE_PRESET_LABEL = Object.freeze({
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
});
export const STATUS_FILTERS = Object.freeze(['Processing', 'Rejected', 'Approved']);
export const PAYMENT_TYPES = Object.freeze(['Deposit', 'Withdrawal', 'Adjustment']);

export function apiBase() {
  return `/api/site/${encodeURIComponent(API_SITE)}/v1`;
}

export function accessToken() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.accessToken || '';
  } catch {
    return '';
  }
}

export function isoDay(value = new Date()) {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function datePresetRange(preset, now = new Date()) {
  const key = DATE_PRESETS.includes(preset) ? preset : 'today';
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (key === 'today') {
    return { preset: key, from: isoDay(today), to: isoDay(today) };
  }
  if (key === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { preset: key, from: isoDay(y), to: isoDay(y) };
  }
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return { preset: 'last7', from: isoDay(from), to: isoDay(today) };
}

export function defaultApplied(now = new Date()) {
  return {
    statuses: [],
    types: [],
    ...datePresetRange('last7', now),
  };
}

export function toggleListValue(list, value) {
  const current = Array.isArray(list) ? list.slice() : [];
  const index = current.indexOf(value);
  if (index >= 0) {
    current.splice(index, 1);
    return current;
  }
  current.push(value);
  return current;
}

export function appliedChips(applied) {
  const chips = [];
  (applied?.statuses || []).forEach((status) => {
    if (STATUS_FILTERS.includes(status)) chips.push({ key: `status:${status}`, label: status });
  });
  (applied?.types || []).forEach((type) => {
    if (PAYMENT_TYPES.includes(type)) chips.push({ key: `type:${type}`, label: type });
  });
  const dateLabel = DATE_PRESET_LABEL[applied?.preset] || DATE_PRESET_LABEL.today;
  chips.push({ key: `date:${applied?.preset || 'today'}`, label: dateLabel });
  return chips;
}

function queryString(params) {
  const t = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === '') return;
    t.set(key, String(value));
  });
  const q = t.toString();
  return q ? `?${q}` : '';
}

async function playerGet(path) {
  const token = accessToken();
  const res = await fetch(`${apiBase()}${path}`, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: 'no-store',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json && json.data !== undefined ? json.data : json;
}

export function transactionRecordsQuery(applied, page, limit = DEFAULT_LIMIT) {
  const params = {
    page,
    limit,
    from: applied?.from,
    to: applied?.to,
  };
  if (applied?.types?.length) params.types = applied.types.join(',');
  if (applied?.statuses?.length) params.statuses = applied.statuses.join(',');
  return params;
}

export async function fetchTransactionRecords(applied, page, limit = DEFAULT_LIMIT) {
  return playerGet(`/transaction-records${queryString(transactionRecordsQuery(applied, page, limit))}`);
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatMoney(value) {
  const n = num(value);
  if (n == null) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}৳ ${Math.abs(n).toLocaleString('en-US')}`;
}

export function amountClass(value) {
  const n = num(value);
  if (n == null || n === 0) return '';
  return n > 0 ? 'tr-amt-pos' : 'tr-amt-neg';
}

export function formatTxnDate(value) {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export function displayStatus(row) {
  if (row?.type === 'Adjustment') return '—';
  const status = String(row?.status || '');
  return status || '—';
}

export function statusClass(row) {
  const status = displayStatus(row);
  if (status === 'Processing') return 'tr-status is-processing';
  if (status === 'Approved') return 'tr-status is-approved';
  if (status === 'Rejected') return 'tr-status is-rejected';
  return 'tr-status is-empty';
}

export function rowKey(row) {
  return `${row?.type || 'row'}:${row?.id || ''}`;
}

export function rowDetails(row) {
  const details = [];
  if (row?.method) details.push(['Payment Method', row.method]);
  if (row?.trxId) details.push(['Txn ID', row.trxId]);
  if (row?.rawStatus) details.push(['Request Status', row.rawStatus]);
  if (row?.note) details.push(['Note', row.note]);
  if (row?.referenceId) details.push(['Reference', row.referenceId]);
  return details;
}
